import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator } from "@frui.ts/validation";
import { Modules, newNavigationName } from "config";
import CreditExposure, { CreditExposureValidationRules } from "entities/creditExposure";
import { computed } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import CreditExposureRepository from "repositories/creditExposureRepository";
import Distributor from "../../../../entities/distributor";
import CreditExposureType from "entities/creditExposureType";
import OptionsService from "../../../../services/optionsService";
import CreateViewModel from "../../../createViewModel";
import type { interfaces } from "inversify";

@Router.registerRoute({ route: newNavigationName, name: Router.Self })
export default class CreditExposureCreateViewModel extends CreateViewModel<CreditExposure> {
  readonly modelName = "creditExposure";

  constructor(
    protected distributorId: string,
    protected repository: CreditExposureRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = newNavigationName;
  }

  protected loadDetail() {
    const item = new CreditExposure();
    attachAutomaticValidator(item, CreditExposureValidationRules);
    const distributor = this.distributorOptions.find(option => option.id === this.distributorId);
    item.distributorId = distributor?.id as string;
    item.distributorName = distributor?.label ?? "";

    return item;
  }

  protected postData() {
    return this.repository.create(this.item, this.distributorId);
  }

  @computed
  get creditExposureTypeOptions() {
    return this.optionsService.internalEnumOptions(CreditExposureType);
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `creditExposures.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (distributorId: string) => {
      return new CreditExposureCreateViewModel(
        distributorId,
        container.get(CreditExposureRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(OptionsService)
      );
    };
  }
}
